//@flow
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { RouteUtil } from 'utils/RouteUtil';
import { isEmpty, groupBy } from 'lodash';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { bindActionCreators } from 'redux';
import { ExploreButton } from 'components/atoms/exploreButton';
import { Title } from 'components/atoms/title';
import HorizontalCarousel from 'components/molecules/horizontalCarousel';
import { ListMapMobile } from 'components/molecules/listMapMobile';
import MapComponent from 'components/molecules/map';
import { PaginationControl } from 'components/molecules/paginationControl';
import { OverlayModal } from 'components/templates/overlayModal';
import { OverlayPortal } from 'components/templates/overlayPortal';
import { useViewport } from 'components/templates/viewportProvider';
import { RentalSelector } from 'modules/rentals/selector';
import { ArrayUtil } from 'utils/ArrayUtil';
import { RentalActions } from 'modules/rentals/actions';
import type { Countries, Project } from 'types/project';
import type { LocationType } from 'types/common';
import './styles.scss';

type Props = {
  data: {
    type: string,
    data: {
      countries: Object<Countries>,
      list: Array<Project>,
    },
    userLocation: Object<LocationType>,
    mapView: boolean,
    updateMapView: () => void,
    product?: Object,
    vertical?: string,
  },
  filteredProjects: Array<Project>,
  activeFilters: Array<string>,
  isFiltered: boolean,
  toggleProjectFilter: (type: string, value: number) => void,
  clearProjectFilters: () => void,
  breadcrumbList: Array<string>,
};

const RentalBrowser = (props: Props) => {
  const { height, isMobile } = useViewport();
  const {
    data,
    filteredProjects,
    isFiltered,
    activeFilters,
    clearRentalFilters,
    breadcrumbList,
  } = props;

  const type = data.type;
  const rentalList = data.data;
  const listToFilter = rentalList;
  const vertical = data.vertical;
  const projectList = isFiltered ? filteredProjects : rentalList;
  const [sliderRef, setSlider] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [rentalOverlay, showRentalOverlay] = useState(false);
  const rentals = groupBy(projectList, 'name');
  let locationCount = Object.keys(rentals).length;
  let houseCount = rentalList.length;
  const updatedList = ArrayUtil.getUniqFormattedData(listToFilter, 'name');

  // clear project filters on unmount
  useEffect(() => {
    return () => {
      clearRentalFilters();
    };
  }, []);

  const goToRentalDetails = list => {
    if (isMobile) {
      if (list.isCompletedProject) {
        RouteUtil.setRouteChange(
          props,
          `/rentals/${list.rental_home_url_slug}`,
          '',
          '',
          list.id
        );
      } else {
        RouteUtil.setRouteChange(
          props,
          `/rentals/${list.projectSlug}/${list.productSlug}/${list.rental_home_url_slug}`,
          '',
          '',
          list.id
        );
      }
    } else !isEmpty(rentalList) && showRentalOverlay(true);
  };

  const updateFilter = value => {
    props.toggleProjectFilter('location', value.cityId);
  };

  const handleSliderRef = ref => {
    setSlider(ref);
  };

  const next = () => {
    sliderRef.slickNext();
  };

  const previous = () => {
    sliderRef.slickPrev();
  };

  const renderRentalOverlay = rentalOverlay ? (
    <OverlayPortal>
      <OverlayModal
        data={isFiltered ? filteredProjects : rentalList}
        theme={isMobile ? 'light-theme' : 'dark-theme'}
        type={'rentals'}
        onClosePress={() => showRentalOverlay(false)}
        breadcrumbList={[
          {
            breadcrumbName: breadcrumbList[1].breadcrumbName,
            breadcrumbLink: breadcrumbList[1].breadcrumbLink,
          },
          {
            breadcrumbName: breadcrumbList[2].breadcrumbName,
            breadcrumbLink: breadcrumbList[2].breadcrumbLink,
          },
        ]}
      />
    </OverlayPortal>
  ) : null;

  const rentalTitle = () => {
    return (
      <Title
        firstText={
          houseCount === 1
            ? `${houseCount} rental home`
            : `${houseCount} rental homes`
        }
        secondText={
          locationCount === 1
            ? `${locationCount} location`
            : `${locationCount} locations`
        }
      />
    );
  };

  const renderFooter = () => {
    const totalLength =
      rentalList.length && rentalList.length > 4 ? rentalList.length - 3 : 1;
    return (
      <Row className={'slider-div'}>
        <Col md={{ offset: 9, span: 3 }}>
          <PaginationControl
            className={'section-footer-item'}
            theme={'light-theme'}
            mode={'slider'}
            currentIndex={activeSlide + 1}
            total={totalLength}
            onNextClick={next}
            onPreviousClick={previous}
          />
        </Col>
      </Row>
    );
  };
  return (
    <div id={'projects'}>
      {isMobile ? (
        <ListMapMobile
          updateFilter={updateFilter}
          data={isFiltered ? filteredProjects : rentalList}
          list={isFiltered ? filteredProjects : rentalList}
          type={'rentals'}
          updateMapView={data.updateMapView}
          fetchTitle={rentalTitle()}
          onClick={goToRentalDetails}
          mapView={data.mapView}
          pinLocation={data.userLocation}
          activeFilters={activeFilters}
          filterType={type}
          vertical={vertical}
        />
      ) : (
        <div className={'project-browser-container'} style={{ height }}>
          <div className={'project-content'}>
            <div className={'map-container'}>
              <MapComponent
                mapStyles={'project-map'}
                data={rentalList}
                // updateFilter={updateFilter}
                type={'rentals'}
                pinLocation={data.userLocation}
                houseCount={houseCount}
              />
            </div>
            <div className={'list-view-container'}>
              <Row className={'list-header'}>
                <Col md={4} sm={4}>
                  {rentalTitle()}
                </Col>
                <Col md={8} sm={8}>
                  <ExploreButton
                    onClick={() => showRentalOverlay(true)}
                    text={'EXPLORE ALL PROJECTS'}
                  />
                </Col>
              </Row>
              <Row className={'list-view-row'}>
                <div className={'list-view'}>
                  <HorizontalCarousel
                    data={updatedList}
                    type={'rentals'}
                    cssFor={'horizontal-carousel-container'}
                    getActiveSlide={e => {
                      setActiveSlide(e);
                    }}
                    handleSliderRef={handleSliderRef}
                    onClick={goToRentalDetails}
                  />
                </div>
              </Row>
            </div>
          </div>
          {projectList.length > 3 && (
            <div className={'project-footer'}>{renderFooter()}</div>
          )}
        </div>
      )}
      {renderRentalOverlay}
    </div>
  );
};

const mapStateToProps = state => {
  return {
    filteredProjects: RentalSelector.getFilteredRentals(state),
    activeFilters: RentalSelector.getActiveFilterNames(state),
    isFiltered: RentalSelector.isRentalFiltered(state),
  };
};

const mapDispatchToProps = dispatch => {
  const { toggleRentalFilter, clearRentalFilters } = RentalActions;
  return bindActionCreators(
    {
      toggleRentalFilter,
      clearRentalFilters,
    },
    dispatch
  );
};

const connectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(RentalBrowser);

export default withRouter(connectedComponent);
